import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  createNotificationConfig,
  deleteNotificationConfig,
  listNotificationConfigs,
  listNotificationDeliveries,
  testNotificationConfig,
  updateNotificationConfig,
  type NotificationConfig,
  type NotificationDelivery,
  type NotificationEvent,
} from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Notifications tab — user-owned outbound webhooks (Slack today, Teams/Discord
 * later). Fire on terminal crawl events + scheduler dispatches. A live config
 * is "subscription = events + webhook URL"; the notifier matches (user, event)
 * at fire time.
 *
 * Layout:
 *   - Create form (inline, expandable)
 *   - Table of existing configs with per-row Pause / Test / Delete
 *   - "Recent deliveries" feed at the bottom — every outbound attempt across
 *     configs, for visibility. Useful for "is Slack receiving us at all?"
 */

const ALL_EVENTS: { id: NotificationEvent; label: string; hint: string }[] = [
  { id: "run.completed", label: "Crawl completed", hint: "Fires when a crawl finishes successfully." },
  { id: "run.failed", label: "Crawl failed", hint: "Fires when a crawl exits with a non-zero code." },
  { id: "schedule.fired", label: "Schedule fired", hint: "Fires when the scheduler dispatches a scheduled crawl." },
];

export function NotificationsPage(): JSX.Element {
  const { user, ready } = useAuth();

  const [configs, setConfigs] = useState<NotificationConfig[] | null>(null);
  const [deliveries, setDeliveries] = useState<NotificationDelivery[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  async function refresh(): Promise<void> {
    try {
      const [c, d] = await Promise.all([
        listNotificationConfigs(),
        listNotificationDeliveries(50),
      ]);
      setConfigs(c);
      setDeliveries(d);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  useEffect(() => {
    if (!ready || !user) return;
    void refresh();
  }, [ready, user]);

  async function handleCreate(input: {
    webhookUrl: string;
    label: string;
    events: NotificationEvent[];
    enabled: boolean;
  }): Promise<void> {
    setError(null);
    setInfo(null);
    setBusy("create");
    try {
      await createNotificationConfig({
        channel: "slack",
        webhookUrl: input.webhookUrl.trim(),
        label: input.label.trim() || null,
        events: input.events,
        enabled: input.enabled,
      });
      setShowCreate(false);
      setInfo("Notification added.");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function handleToggle(row: NotificationConfig): Promise<void> {
    setError(null);
    setInfo(null);
    setBusy(row.id);
    try {
      await updateNotificationConfig(row.id, { enabled: !row.enabled });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function handleTest(row: NotificationConfig): Promise<void> {
    setError(null);
    setInfo(null);
    setBusy(row.id);
    try {
      const res = await testNotificationConfig(row.id);
      const d = res.delivered;
      if (d && d.status === "ok") {
        setInfo(`Test delivered (${d.httpStatus}).`);
      } else if (d && d.status === "error") {
        setError(
          `Test failed${d.httpStatus ? ` (HTTP ${d.httpStatus})` : ""}${
            d.errorMessage ? `: ${d.errorMessage}` : ""
          }.`,
        );
      } else {
        setInfo("Test fired.");
      }
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete(row: NotificationConfig): Promise<void> {
    const what = row.label ? `"${row.label}"` : `config ${row.id.slice(0, 8)}`;
    if (!confirm(`Delete Slack notification ${what}? This can't be undone.`)) return;
    setError(null);
    setInfo(null);
    setBusy(row.id);
    try {
      await deleteNotificationConfig(row.id);
      await refresh();
      setInfo("Notification removed.");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  if (ready && !user) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900 p-6 text-sm text-slate-300">
        <p className="font-medium text-slate-100">Sign in to manage notifications</p>
        <p className="mt-1 text-slate-400">
          Notifications are user-owned — the notifier fires against your
          configs only when a crawl of yours finishes.
        </p>
        <Link
          to="/sign-in?redirect=/notifications"
          className="mt-4 inline-block rounded-md bg-indigo-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-400"
        >
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <div>
      <header className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Notifications</h1>
          <p className="mt-1 text-sm text-slate-400">
            Slack Incoming Webhooks fire on crawl finish, crawl failure, and
            scheduled-run dispatch. Configs are per-user.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-500 font-mono">api: /api/notifications</div>
          <button
            type="button"
            onClick={() => {
              setShowCreate((v) => !v);
              setInfo(null);
              setError(null);
            }}
            className="rounded-md bg-indigo-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-400"
          >
            {showCreate ? "Cancel" : "+ Add Slack webhook"}
          </button>
        </div>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-700 bg-rose-900/40 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      )}
      {info && (
        <div className="mb-4 rounded-md border border-emerald-800 bg-emerald-900/30 px-4 py-3 text-sm text-emerald-200">
          {info}
        </div>
      )}

      {showCreate && (
        <CreateNotificationForm
          busy={busy === "create"}
          onSubmit={handleCreate}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {configs === null && !error && (
        <div className="rounded-lg border border-slate-800 bg-slate-900 p-6 text-sm text-slate-400">
          Loading…
        </div>
      )}

      {configs && configs.length === 0 && !showCreate && (
        <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center">
          <p className="text-slate-300">No notifications configured yet.</p>
          <p className="mt-1 text-sm text-slate-500">
            Paste a Slack Incoming Webhook URL and pick which events should
            ping your channel.
          </p>
        </div>
      )}

      {configs && configs.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Channel / Label</th>
                <th className="px-4 py-3">Webhook</th>
                <th className="px-4 py-3">Events</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 bg-slate-950">
              {configs.map((row) => (
                <ConfigRow
                  key={row.id}
                  row={row}
                  busy={busy === row.id}
                  onToggle={() => handleToggle(row)}
                  onTest={() => handleTest(row)}
                  onDelete={() => handleDelete(row)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent deliveries feed — same shape across configs. */}
      <section className="mt-8">
        <h2 className="mb-3 text-sm font-semibold text-slate-300">
          Recent deliveries
        </h2>
        {deliveries === null ? (
          <div className="rounded-lg border border-slate-800 bg-slate-900 p-4 text-sm text-slate-500">
            Loading…
          </div>
        ) : deliveries.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-6 text-center text-sm text-slate-500">
            Nothing delivered yet. Fire a <em>Test</em> above to prove the
            pipeline works.
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-800">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2">When</th>
                  <th className="px-4 py-2">Event</th>
                  <th className="px-4 py-2">Config</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">HTTP</th>
                  <th className="px-4 py-2">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800 bg-slate-950">
                {deliveries.map((d) => (
                  <tr key={d.id} className="text-slate-300">
                    <td className="px-4 py-2 text-slate-400">
                      {formatTs(d.deliveredAt, "—")}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-400">
                      {d.event}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">
                      {configs?.find((c) => c.id === d.configId)?.label ??
                        d.configId.slice(0, 8)}
                    </td>
                    <td className="px-4 py-2">
                      {d.status === "ok" ? (
                        <span className="rounded-full bg-emerald-900/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-300">
                          ok
                        </span>
                      ) : (
                        <span className="rounded-full bg-rose-900/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-rose-300">
                          error
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-slate-400">
                      {d.httpStatus ?? "—"}
                    </td>
                    <td
                      className="px-4 py-2 text-xs text-slate-500 truncate max-w-xs"
                      title={d.errorMessage ?? ""}
                    >
                      {d.errorMessage ?? ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function ConfigRow(props: {
  row: NotificationConfig;
  busy: boolean;
  onToggle: () => void;
  onTest: () => void;
  onDelete: () => void;
}): JSX.Element {
  const { row, busy } = props;
  return (
    <tr className="text-slate-200">
      <td className="px-4 py-3">
        <div className="font-medium">
          {row.label ?? <span className="text-slate-400">(no label)</span>}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">
          {row.channel}
        </div>
      </td>
      <td className="px-4 py-3 font-mono text-xs text-slate-400">
        {shortenWebhook(row.webhookUrl)}
      </td>
      <td className="px-4 py-3">
        {row.events.length === 0 ? (
          <span className="text-xs text-slate-500">(none — row inactive)</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {row.events.map((e) => (
              <span
                key={e}
                className="rounded border border-slate-700 bg-slate-900 px-2 py-0.5 text-[10px] text-slate-300"
              >
                {e}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        {row.enabled ? (
          <span className="rounded-full bg-emerald-900/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-300">
            enabled
          </span>
        ) : (
          <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-400">
            paused
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <button
          type="button"
          onClick={props.onTest}
          disabled={busy}
          className="mr-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-300 hover:border-slate-600 disabled:opacity-50"
        >
          Test
        </button>
        <button
          type="button"
          onClick={props.onToggle}
          disabled={busy}
          className="mr-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-300 hover:border-slate-600 disabled:opacity-50"
        >
          {row.enabled ? "Pause" : "Resume"}
        </button>
        <button
          type="button"
          onClick={props.onDelete}
          disabled={busy}
          className="rounded-md border border-rose-900 bg-rose-950/40 px-2 py-1 text-xs text-rose-300 hover:border-rose-800 disabled:opacity-50"
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

// ─────────────────────── Create form ───────────────────────

function CreateNotificationForm(props: {
  busy: boolean;
  onSubmit: (input: {
    webhookUrl: string;
    label: string;
    events: NotificationEvent[];
    enabled: boolean;
  }) => void;
  onCancel: () => void;
}): JSX.Element {
  const [webhookUrl, setWebhookUrl] = useState("");
  const [label, setLabel] = useState("");
  const [events, setEvents] = useState<NotificationEvent[]>([
    "run.completed",
    "run.failed",
  ]);
  const [enabled, setEnabled] = useState(true);

  function toggle(e: NotificationEvent): void {
    setEvents((prev) =>
      prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e],
    );
  }

  function submit(ev: React.FormEvent<HTMLFormElement>): void {
    ev.preventDefault();
    props.onSubmit({ webhookUrl, label, events, enabled });
  }

  return (
    <form
      onSubmit={submit}
      className="mb-6 rounded-lg border border-slate-800 bg-slate-900/60 p-4"
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="block text-sm md:col-span-2">
          <span className="mb-1 block text-slate-400">
            Slack Incoming Webhook URL{" "}
            <span className="text-xs text-slate-500">
              (starts with <code>https://hooks.slack.com/…</code>)
            </span>
          </span>
          <input
            required
            type="url"
            pattern="https://.*"
            placeholder="https://hooks.slack.com/services/T000/B000/XXXX"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-xs text-slate-100"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-slate-400">
            Label <span className="text-xs text-slate-500">(optional)</span>
          </span>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="#qa-bots"
            className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-slate-400">Enabled</span>
          <select
            value={enabled ? "1" : "0"}
            onChange={(e) => setEnabled(e.target.value === "1")}
            className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
          >
            <option value="1">Active</option>
            <option value="0">Paused</option>
          </select>
        </label>
      </div>
      <fieldset className="mt-4">
        <legend className="mb-2 text-sm text-slate-400">Fire on events</legend>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          {ALL_EVENTS.map((ev) => (
            <label
              key={ev.id}
              className="flex items-start gap-2 rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm"
            >
              <input
                type="checkbox"
                checked={events.includes(ev.id)}
                onChange={() => toggle(ev.id)}
                className="mt-1"
              />
              <span>
                <span className="block text-slate-200">{ev.label}</span>
                <span className="block text-xs text-slate-500">{ev.hint}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={props.onCancel}
          className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-300 hover:border-slate-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={props.busy}
          className="rounded-md bg-indigo-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-400 disabled:opacity-50"
        >
          {props.busy ? "Saving…" : "Add notification"}
        </button>
      </div>
    </form>
  );
}

// ─────────────────────── Helpers ───────────────────────

function shortenWebhook(url: string): string {
  try {
    const u = new URL(url);
    // Slack URLs are of the form hooks.slack.com/services/T<team>/B<channel>/<secret>
    // Show host + redact the secret segment.
    const parts = u.pathname.split("/").filter(Boolean);
    if (parts.length >= 3) {
      const redacted = [...parts.slice(0, -1), "••••"].join("/");
      return `${u.host}/${redacted}`;
    }
    return `${u.host}${u.pathname}`;
  } catch {
    return url;
  }
}

function formatTs(ms: number | null, fallback: string): string {
  if (ms === null) return fallback;
  const d = new Date(ms);
  const now = Date.now();
  const delta = now - ms;
  if (delta < 60_000) return "just now";
  const mins = Math.round(delta / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
}
